// pages/MyOrders.tsx
import { useState, useEffect } from 'react';
import {
    FaBox,
    FaCheckCircle,
    FaClock,
    FaTruck,
    FaTimesCircle,
    FaArrowLeft,
    FaShoppingBag,
    FaEye,
    FaDownload,
    FaPrint,
    FaChevronDown,
    FaChevronUp,
} from 'react-icons/fa';
import { ScrollToTop } from '@/components/ScrollToTop';
import { ArrowLeft } from 'lucide-react';

interface OrderItem {
    id: number;
    name: string;
    price: number;
    quantity: number;
    image: string;
}

interface Order {
    id: string;
    orderId: string;
    items: OrderItem[];
    total: number;
    status: 'pending' | 'processing' | 'shipped' | 'delivered' | 'cancelled';
    paymentMethod: string;
    date: string;
    customer: {
        firstName: string;
        lastName: string;
        email: string;
        phone: string;
        address: string;
        city: string;
        state: string;
        zipCode: string;
        country: string;
    };
}

interface MyOrdersProps {
    onBack?: () => void;
    isLoggedIn?: boolean;
    setIsAuthModalOpen?: React.Dispatch<React.SetStateAction<boolean>>;
}

export default function MyOrders({ onBack, isLoggedIn = false, setIsAuthModalOpen }: MyOrdersProps) {
    const [orders, setOrders] = useState<Order[]>([]);
    const [selectedOrder, setSelectedOrder] = useState<string | null>(null);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState<string>('all');

    // Load orders from localStorage
    useEffect(() => {
        const loadOrders = () => {
            try {
                const savedOrders = localStorage.getItem('shopOrders');
                if (savedOrders) {
                    const parsedOrders = JSON.parse(savedOrders);
                    // Sort by date (newest first)
                    parsedOrders.sort((a: Order, b: Order) =>
                        new Date(b.date).getTime() - new Date(a.date).getTime()
                    );
                    setOrders(parsedOrders);
                }
            } catch (error) {
                console.error('Error loading orders:', error);
            } finally {
                setLoading(false);
            }
        };

        loadOrders();

        // Listen for storage changes (if orders are updated elsewhere)
        const handleStorageChange = (e: StorageEvent) => {
            if (e.key === 'shopOrders') {
                loadOrders();
            }
        };
        window.addEventListener('storage', handleStorageChange);
        return () => window.removeEventListener('storage', handleStorageChange);
    }, []);

    // Get status icon and color
    const getStatusInfo = (status: Order['status']) => {
        const statusMap = {
            pending: {
                icon: <FaClock className="text-yellow-500" />,
                color: 'bg-yellow-100 text-yellow-800',
                label: 'Pending',
            },
            processing: {
                icon: <FaBox className="text-blue-500" />,
                color: 'bg-blue-100 text-blue-800',
                label: 'Processing',
            },
            shipped: {
                icon: <FaTruck className="text-purple-500" />,
                color: 'bg-purple-100 text-purple-800',
                label: 'Shipped',
            },
            delivered: {
                icon: <FaCheckCircle className="text-green-500" />,
                color: 'bg-green-100 text-green-800',
                label: 'Delivered',
            },
            cancelled: {
                icon: <FaTimesCircle className="text-red-500" />,
                color: 'bg-red-100 text-red-800',
                label: 'Cancelled',
            },
        };
        return statusMap[status] || statusMap.pending;
    };

    // Format date
    const formatDate = (dateString: string) => {
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
        });
    };

    // Get filtered orders
    const getFilteredOrders = () => {
        if (filter === 'all') return orders;
        return orders.filter(order => order.status === filter);
    };

    // Toggle order details
    const toggleOrderDetails = (orderId: string) => {
        setSelectedOrder(selectedOrder === orderId ? null : orderId);
    };



    // if (!isLoggedIn) {

    //     return (

    //         <div className="min-h-screen bg-gray-50 py-20">
    //             <div className="container mx-auto max-w-4xl px-4">
    //                 {/* Back Button */}
    //                 <button

    //                     onClick={() => onBack?.()}

    //                     className="mb-6 inline-flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors"

    //                 >

    //                     <ArrowLeft size={18} />

    //                     Back

    //                 </button>

    //                 <div className="bg-white rounded-3xl shadow-2xl p-12 text-center">

    //                     <div className="text-6xl mb-6">🔒</div>

    //                     <h2 className="font-primary text-3xl font-bold text-gray-800 mb-4">
    //                         Login Required
    //                     </h2>

    //                     <p className="font-secondary text-gray-500 max-w-md mx-auto mb-8">

    //                         Please login to view your orders and track your purchases.

    //                     </p>

    //                     <div className="flex flex-col sm:flex-row gap-4 justify-center">


    //                         <button

    //                             onClick={() => {

    //                                 onBack?.(); // close orders overlay

    //                                 setIsAuthModalOpen?.(true); // open login modal

    //                             }}

    //                             className="px-8 py-3 bg-blue-600 text-white rounded-xl"

    //                         >

    //                             Login Now

    //                         </button>

    //                     </div>

    //                 </div>

    //             </div>

    //         </div>

    //     );

    // }

    const filteredOrders = getFilteredOrders();

    return (
        <div className="min-h-screen bg-gray-50 py-8 md:py-12">
            <ScrollToTop />

            <div className="container mx-auto max-w-6xl px-4">
                {/* Header */}
                <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4 mb-8">
                    <div>
                        <button
                            onClick={() => onBack?.()}
                            className="inline-flex items-center gap-2 text-gray-500 hover:text-gray-700 transition-colors duration-200 mb-2"
                        >
                            <FaArrowLeft className="text-sm" />
                            Back to Shopping
                        </button>
                        <h1 className="font-primary text-3xl md:text-4xl font-bold text-gray-800">
                            My Orders
                        </h1>
                        <p className="font-secondary text-gray-500 mt-1">
                            Track and manage all your orders in one place
                        </p>
                    </div>
                    <div className="flex items-center gap-3">
                        <span className="bg-blue-100 text-blue-600 px-4 py-2 rounded-full text-sm font-semibold">
                            {orders.length} Orders
                        </span>
                    </div>
                </div>

                {/* Filters */}
                <div className="flex flex-wrap gap-2 mb-6">
                    {[
                        { id: 'all', label: 'All Orders' },
                        { id: 'pending', label: 'Pending' },
                        { id: 'processing', label: 'Processing' },
                        { id: 'shipped', label: 'Shipped' },
                        { id: 'delivered', label: 'Delivered' },
                        { id: 'cancelled', label: 'Cancelled' },
                    ].map((filterOption) => (
                        <button
                            key={filterOption.id}
                            onClick={() => setFilter(filterOption.id)}
                            className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 ${filter === filterOption.id
                                ? 'bg-blue-600 text-white shadow-md'
                                : 'bg-white text-gray-600 hover:bg-gray-100'
                                }`}
                        >
                            {filterOption.label}
                        </button>
                    ))}
                </div>

                {/* Loading State */}
                {loading ? (
                    <div className="flex justify-center items-center py-20">
                        <div className="text-center">
                            <div className="animate-spin rounded-full h-12 w-12 border-4 border-blue-600 border-t-transparent mx-auto mb-4"></div>
                            <p className="font-secondary text-gray-500">Loading your orders...</p>
                        </div>
                    </div>
                ) : orders.length === 0 ? (
                    // No Orders State
                    <div className="bg-white rounded-3xl shadow-xl p-12 text-center">
                        <div className="text-6xl mb-6">📦</div>
                        <h2 className="font-primary text-2xl font-bold text-gray-800 mb-2">
                            No Orders Yet
                        </h2>
                        <p className="font-secondary text-gray-500 max-w-md mx-auto">
                            You haven't placed any orders yet. Start shopping to see your orders here.
                        </p>
                        <button
                            onClick={() => onBack?.()}
                            className="mt-6 px-8 py-3 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white rounded-xl font-primary font-semibold transition-all duration-300 shadow-lg hover:shadow-xl transform hover:scale-105"
                        >
                            Start Shopping
                        </button>
                    </div>
                ) : filteredOrders.length === 0 ? (
                    // No filtered orders
                    <div className="bg-white rounded-3xl shadow-xl p-12 text-center">
                        <div className="text-6xl mb-6">🔍</div>
                        <h2 className="font-primary text-2xl font-bold text-gray-800 mb-2">
                            No Orders Found
                        </h2>
                        <p className="font-secondary text-gray-500 max-w-md mx-auto">
                            No orders found with the selected filter. Try changing your filter.
                        </p>
                        <button
                            onClick={() => setFilter('all')}
                            className="mt-6 px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors duration-200"
                        >
                            View All Orders
                        </button>
                    </div>
                ) : (
                    // Orders List
                    <div className="space-y-4">
                        {filteredOrders.map((order) => {
                            const statusInfo = getStatusInfo(order.status);
                            const isExpanded = selectedOrder === order.id;

                            return (
                                <div
                                    key={order.id}
                                    className="bg-white rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 overflow-hidden"
                                >
                                    {/* Order Header */}
                                    <div
                                        className="p-6 cursor-pointer hover:bg-gray-50/50 transition-colors duration-200"
                                        onClick={() => toggleOrderDetails(order.id)}
                                    >
                                        <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
                                            <div className="flex items-center gap-4">
                                                <div className="w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center text-gray-500">
                                                    <FaShoppingBag className="text-xl" />
                                                </div>
                                                <div>
                                                    <p className="font-primary font-semibold text-gray-800">
                                                        {order.orderId}
                                                    </p>
                                                    <p className="font-secondary text-sm text-gray-500">
                                                        {formatDate(order.date)}
                                                    </p>
                                                </div>
                                            </div>
                                            <div className="flex items-center gap-4">
                                                <div className={`px-3 py-1 rounded-full ${statusInfo.color} flex items-center gap-1.5 text-sm font-medium`}>
                                                    {statusInfo.icon}
                                                    {statusInfo.label}
                                                </div>
                                                <span className="font-primary font-bold text-lg text-blue-600">
                                                    ${order.total.toFixed(2)}
                                                </span>
                                                <div className="text-gray-400">
                                                    {isExpanded ? <FaChevronUp /> : <FaChevronDown />}
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Order Details (Expanded) */}
                                    {isExpanded && (
                                        <div className="border-t border-gray-100 p-6 bg-gray-50/30">
                                            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                                                {/* Items */}
                                                <div>
                                                    <h4 className="font-primary font-semibold text-gray-800 mb-3 flex items-center gap-2">
                                                        <FaBox className="text-blue-500" />
                                                        Items
                                                    </h4>
                                                    <div className="space-y-3">
                                                        {order.items.map((item) => (
                                                            <div key={item.id} className="flex items-center gap-3 p-3 bg-white rounded-xl shadow-sm">
                                                                <img
                                                                    src={item.image}
                                                                    alt={item.name}
                                                                    className="w-16 h-16 object-cover rounded-lg"
                                                                />
                                                                <div className="flex-1">
                                                                    <p className="font-primary font-semibold text-sm text-gray-800">
                                                                        {item.name}
                                                                    </p>
                                                                    <p className="font-secondary text-sm text-gray-500">
                                                                        Qty: {item.quantity} × ${item.price.toFixed(2)}
                                                                    </p>
                                                                </div>
                                                                <span className="font-primary font-bold text-blue-600">
                                                                    ${(item.price * item.quantity).toFixed(2)}
                                                                </span>
                                                            </div>
                                                        ))}
                                                    </div>
                                                </div>

                                                {/* Order Details */}
                                                <div>
                                                    <h4 className="font-primary font-semibold text-gray-800 mb-3 flex items-center gap-2">
                                                        <FaBox className="text-blue-500" />
                                                        Order Details
                                                    </h4>
                                                    <div className="bg-white rounded-xl p-4 space-y-2 shadow-sm">
                                                        <div className="flex justify-between">
                                                            <span className="font-secondary text-sm text-gray-500">Order ID</span>
                                                            <span className="font-secondary text-sm font-medium text-gray-800">{order.orderId}</span>
                                                        </div>
                                                        <div className="flex justify-between">
                                                            <span className="font-secondary text-sm text-gray-500">Date</span>
                                                            <span className="font-secondary text-sm font-medium text-gray-800">{formatDate(order.date)}</span>
                                                        </div>
                                                        <div className="flex justify-between">
                                                            <span className="font-secondary text-sm text-gray-500">Payment</span>
                                                            <span className="font-secondary text-sm font-medium text-gray-800">{order.paymentMethod}</span>
                                                        </div>
                                                        <div className="flex justify-between">
                                                            <span className="font-secondary text-sm text-gray-500">Status</span>
                                                            <span className={`font-secondary text-sm font-medium ${statusInfo.color} px-2 py-0.5 rounded-full`}>
                                                                {statusInfo.label}
                                                            </span>
                                                        </div>
                                                        <div className="border-t border-gray-100 pt-2 flex justify-between">
                                                            <span className="font-primary font-bold text-gray-800">Total</span>
                                                            <span className="font-primary font-bold text-xl text-blue-600">${order.total.toFixed(2)}</span>
                                                        </div>
                                                    </div>

                                                    {/* Shipping Address */}
                                                    <div className="mt-4">
                                                        <h5 className="font-secondary text-sm font-semibold text-gray-700 mb-2">Shipping Address</h5>
                                                        <div className="bg-white rounded-xl p-4 shadow-sm">
                                                            <p className="font-secondary text-sm text-gray-600">
                                                                {order.customer.firstName} {order.customer.lastName}
                                                            </p>
                                                            <p className="font-secondary text-sm text-gray-600">{order.customer.address}</p>
                                                            <p className="font-secondary text-sm text-gray-600">
                                                                {order.customer.city}, {order.customer.state} {order.customer.zipCode}
                                                            </p>
                                                            <p className="font-secondary text-sm text-gray-600">{order.customer.country}</p>
                                                            <p className="font-secondary text-sm text-gray-600 mt-1">{order.customer.phone}</p>
                                                            <p className="font-secondary text-sm text-gray-600">{order.customer.email}</p>
                                                        </div>
                                                    </div>

                                                    {/* Action Buttons */}
                                                    <div className="flex flex-wrap gap-3 mt-4">
                                                        <button className="flex-1 bg-blue-600 hover:bg-blue-700 text-white font-primary font-semibold py-2 rounded-lg transition-colors duration-200 flex items-center justify-center gap-2">
                                                            <FaEye className="text-sm" />
                                                            Track Order
                                                        </button>
                                                        <button className="flex-1 bg-gray-200 hover:bg-gray-300 text-gray-700 font-primary font-semibold py-2 rounded-lg transition-colors duration-200 flex items-center justify-center gap-2">
                                                            <FaDownload className="text-sm" />
                                                            Invoice
                                                        </button>
                                                        <button className="flex-1 bg-gray-200 hover:bg-gray-300 text-gray-700 font-primary font-semibold py-2 rounded-lg transition-colors duration-200 flex items-center justify-center gap-2">
                                                            <FaPrint className="text-sm" />
                                                            Print
                                                        </button>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                )}

                {/* Order Summary */}
                {orders.length > 0 && (
                    <div className="mt-8 bg-white rounded-2xl shadow-lg p-6">
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                            <div className="text-center">
                                <p className="font-secondary text-sm text-gray-500">Total Orders</p>
                                <p className="font-primary text-2xl font-bold text-gray-800">{orders.length}</p>
                            </div>
                            <div className="text-center">
                                <p className="font-secondary text-sm text-gray-500">Total Spent</p>
                                <p className="font-primary text-2xl font-bold text-blue-600">
                                    ${orders.reduce((sum, order) => sum + order.total, 0).toFixed(2)}
                                </p>
                            </div>
                            <div className="text-center">
                                <p className="font-secondary text-sm text-gray-500">Delivered</p>
                                <p className="font-primary text-2xl font-bold text-green-600">
                                    {orders.filter(o => o.status === 'delivered').length}
                                </p>
                            </div>
                            <div className="text-center">
                                <p className="font-secondary text-sm text-gray-500">In Progress</p>
                                <p className="font-primary text-2xl font-bold text-blue-600">
                                    {orders.filter(o => o.status !== 'delivered' && o.status !== 'cancelled').length}
                                </p>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}